import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { FileSpreadsheet, FileText, Loader2 } from 'lucide-react';
import { downloadFile } from '@/lib/download';

const API = `${window.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || ''}/api`;

// Mismas claves que backend/utils/player_listing.py
const COLUMN_OPTIONS = [
  ['last_name', 'Apellido'], ['first_name', 'Nombre'], ['birth_date', 'Fecha de nacimiento'], ['age', 'Edad'],
  ['category', 'Categoría'], ['position', 'Posición'], ['preferred_foot', 'Pierna hábil'], ['origin_club', 'Club'],
  ['scout', 'Captador'], ['status', 'Estado'], ['overall_score', 'Puntaje'], ['nationality', 'Nacionalidad'],
  ['join_date', 'Fecha de ingreso'], ['document', 'Cédula'], ['phone', 'Teléfono'], ['email', 'Email'],
  ['family', 'Contacto familiar'],
];
const SENSITIVE = new Set(['document', 'phone', 'email', 'family']);
const DEFAULT_COLUMNS = ['last_name', 'first_name', 'birth_date', 'age', 'category', 'position', 'origin_club', 'scout', 'status'];

/**
 * Descarga el listado de jugadores con los filtros que están aplicados en pantalla.
 * `buildParams()` devuelve los filtros actuales; `total` es la cantidad de jugadores que coinciden.
 */
const PlayersExportDialog = ({ open, onOpenChange, buildParams, total, filtersText, isScout }) => {
  const [columns, setColumns] = useState(DEFAULT_COLUMNS);
  const [busy, setBusy] = useState(null);

  const toggle = (key) => setColumns((cur) => (cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key]));

  const download = async (format) => {
    if (columns.length === 0) return;
    setBusy(format);
    const params = buildParams();
    params.set('format', format);
    // se respeta el orden de COLUMN_OPTIONS
    params.set('columns', COLUMN_OPTIONS.map(([k]) => k).filter((k) => columns.includes(k)).join(','));
    const okay = await downloadFile(`${API}/players/export-list`, { params, fallbackName: `jugadores.${format}` });
    setBusy(null);
    if (okay) onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="export-list-dialog">
        <DialogHeader>
          <DialogTitle>Descargar listado de jugadores</DialogTitle>
          <DialogDescription>
            Se descarga lo que está filtrado en pantalla: <strong data-testid="export-list-total">{total}</strong> jugador(es).
            {' '}Si no hay filtros, sale el listado completo.
          </DialogDescription>
        </DialogHeader>
        <p className="text-sm text-slate-600" data-testid="export-list-filters">Filtros: {filtersText || 'ninguno (todos los jugadores)'}</p>
        <div>
          <p className="text-sm font-medium mb-2">Columnas</p>
          <div className="grid grid-cols-2 gap-x-4 gap-y-2">
            {COLUMN_OPTIONS.map(([key, label]) => (
              <div key={key} className="flex items-center gap-2">
                <Checkbox id={`col-${key}`} checked={columns.includes(key)} onCheckedChange={() => toggle(key)} data-testid={`col-${key}`} />
                <Label htmlFor={`col-${key}`} className="text-sm font-normal cursor-pointer">{label}</Label>
              </div>
            ))}
          </div>
          {isScout && (
            <p className="text-xs text-slate-500 mt-2">Cédula, teléfono, email y contacto familiar solo salen de tus propios jugadores.</p>
          )}
          {!isScout && columns.some((k) => SENSITIVE.has(k)) && (
            <p className="text-xs text-amber-700 mt-2">Incluye datos personales: cuidá con quién compartís el archivo.</p>
          )}
        </div>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" disabled={!!busy || columns.length === 0 || total === 0} onClick={() => download('pdf')} data-testid="export-list-pdf">
            {busy === 'pdf' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />} PDF (imprimir)
          </Button>
          <Button disabled={!!busy || columns.length === 0 || total === 0} onClick={() => download('xlsx')} className="bg-brand-600 hover:bg-brand-700" data-testid="export-list-xlsx">
            {busy === 'xlsx' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-2" />} Excel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PlayersExportDialog;
